import type { Tenant, WorkspaceThemeId } from '~/types/models';

type Rgb = {
    r: number;
    g: number;
    b: number;
};

export type WorkspaceBrandTheme = {
    primary: string;
    primaryForeground: string;
    accent: string;
    accentForeground: string;
    surface: string;
    surfaceMuted: string;
    background: string;
    foreground: string;
    ring: string;
    sidebar: string;
    sidebarAccent: string;
    sidebarForeground: string;
    portalGlow: string;
    darkBackground: string;
    darkForeground: string;
    darkSurface: string;
    darkSurfaceMuted: string;
    darkRing: string;
    darkSidebar: string;
    darkSidebarAccent: string;
    darkSidebarForeground: string;
    darkPrimary?: string;
    darkPrimaryForeground?: string;
    darkAccent?: string;
    darkAccentForeground?: string;
    border?: string;
    borderStrong?: string;
    mutedForeground?: string;
    primarySoft?: string;
    accentSoft?: string;
    darkBorder?: string;
    darkBorderStrong?: string;
    darkMutedForeground?: string;
    darkPrimarySoft?: string;
    darkAccentSoft?: string;
    displayFontFamily?: string;
    bodyFontFamily?: string;
    metadataFontFamily?: string;
    atmosphereImage?: string;
    atmosphereColor?: string;
    darkAtmosphereColor?: string;
    atmosphereOpacity?: number;
    darkAtmosphereOpacity?: number;
    radius?: string;
    controlRadius?: string;
    surfaceShadow?: string;
    darkSurfaceShadow?: string;
    chartColors?: [string, string, string, string, string];
    darkChartColors?: [string, string, string, string, string];
    entityArtworkColors?: [string, string, string, string, string, string];
    darkEntityArtworkColors?: [string, string, string, string, string, string];
    entityArtworkImage?: string;
};

export type WorkspaceAppThemeDefinition = {
    id: 'studio' | 'coffee-house' | 'editorial' | 'stone';
    label: string;
    description: string;
    personality: string;
    theme: WorkspaceBrandTheme;
};

export type BrandSurfacePalette = {
    seed: string;
    background: string;
    surface: string;
    surfaceMuted: string;
    darkBackground: string;
    darkSurface: string;
    darkSurfaceMuted: string;
};

export type PortalFontOption = {
    family: string;
    label: string;
    description: string;
    category?: string;
};

const INTER_FONT_FAMILY = '"Inter Variable", "Inter", system-ui, sans-serif';
const KOHO_FONT_FAMILY = '"KoHo", "Inter Variable", "Inter", system-ui, sans-serif';
const SYSTEM_FONT_FAMILY = 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
const DEFAULT_PORTAL_SURFACE_SEED = '#d8d1c5';

export const DEFAULT_PORTAL_DISPLAY_FONT_FAMILY = 'KoHo';
export const DEFAULT_PORTAL_BODY_FONT_FAMILY = 'Inter';

export const PORTAL_FONT_FALLBACK_OPTIONS: ReadonlyArray<PortalFontOption> = [
    { family: 'KoHo', label: 'KoHo', description: 'Bundled with Vuey.', category: 'Sans Serif' },
    { family: 'Inter', label: 'Inter', description: 'Bundled with Vuey.', category: 'Sans Serif' },
    { family: 'System UI', label: 'System UI', description: 'Uses the client device typography.', category: 'Sans Serif' },
    { family: 'Raleway', label: 'Raleway', description: 'Google Fonts', category: 'Sans Serif' },
    { family: 'EB Garamond', label: 'EB Garamond', description: 'Google Fonts', category: 'Serif' },
];

const LEGACY_PORTAL_FONT_FAMILIES: Record<string, string> = {
    inter: 'Inter',
    koho: 'KoHo',
    system: 'System UI',
    'dm-sans': 'DM Sans',
    manrope: 'Manrope',
    'plus-jakarta-sans': 'Plus Jakarta Sans',
    montserrat: 'Montserrat',
    poppins: 'Poppins',
    outfit: 'Outfit',
    'space-grotesk': 'Space Grotesk',
    'playfair-display': 'Playfair Display',
    lora: 'Lora',
    merriweather: 'Merriweather',
    'cormorant-garamond': 'Cormorant Garamond',
    'libre-baskerville': 'Libre Baskerville',
};

// Tenant-facing branding keeps its own neutral fallback. Staff app themes must
// never change this object because portal appearance belongs to the tenant.
const DEFAULT_PORTAL_THEME: WorkspaceBrandTheme = {
    primary: '#6969b3',
    primaryForeground: '#f8f4e3',
    accent: '#ff8966',
    accentForeground: '#2a2b2a',
    surface: '#fffdfa',
    surfaceMuted: '#f6f0e4',
    background: '#fafaf8',
    foreground: '#2a2b2a',
    ring: '#a2a2d6',
    sidebar: '#f4efe4',
    sidebarAccent: '#ede5d8',
    sidebarForeground: '#2a2b2a',
    portalGlow: '#f4e9de',
    darkBackground: '#2a2b2a',
    darkForeground: '#f8f4e3',
    darkSurface: '#333432',
    darkSurfaceMuted: '#403d38',
    darkRing: '#9b9cd9',
    darkSidebar: '#242523',
    darkSidebarAccent: '#32302d',
    darkSidebarForeground: '#f8f4e3',
};

// Staff themes are personal working environments. Components consume their
// semantic values through shadcn tokens rather than reading palette colours.
const STUDIO_APP_THEME: WorkspaceBrandTheme = {
    primary: '#6969b3',
    primaryForeground: '#ffffff',
    accent: '#ff8966',
    accentForeground: '#382019',
    surface: '#ffffff',
    surfaceMuted: '#f7f6fc',
    background: '#F9FAFB',
    foreground: '#24222b',
    ring: '#7f79d4',
    sidebar: '#f7f6fc',
    sidebarAccent: '#6969b3',
    sidebarForeground: '#24222b',
    portalGlow: '#ecebfa',
    darkBackground: '#17161c',
    darkForeground: '#f7f5fa',
    darkSurface: '#201e26',
    darkSurfaceMuted: '#292630',
    darkRing: '#a59ff0',
    darkSidebar: '#201e26',
    darkSidebarAccent: '#292630',
    darkSidebarForeground: '#f7f5fa',
    darkPrimary: '#908be5',
    darkPrimaryForeground: '#17161c',
    darkAccent: '#ff9475',
    darkAccentForeground: '#2a1711',
    border: '#e9e7f2',
    borderStrong: '#dcd8e8',
    mutedForeground: '#716d78',
    primarySoft: '#ecebfa',
    accentSoft: '#fff0eb',
    darkBorder: '#37333f',
    darkBorderStrong: '#484250',
    darkMutedForeground: '#aaa5b1',
    darkPrimarySoft: '#302c50',
    darkAccentSoft: '#472c27',
    displayFontFamily: 'Bricolage Grotesque',
    bodyFontFamily: 'DM Sans',
    metadataFontFamily: 'DM Mono',
    atmosphereImage: '/theme-atmospheres/vuey.svg',
    atmosphereColor: '#6969b3',
    darkAtmosphereColor: '#908be5',
    atmosphereOpacity: 0.15,
    darkAtmosphereOpacity: 0.135,
    radius: '1rem',
    controlRadius: '0.75rem',
    surfaceShadow: 'none',
    darkSurfaceShadow: 'none',
    chartColors: ['#6969b3', '#ff8966', '#8a9577', '#c98059', '#a69bdb'],
    darkChartColors: ['#908be5', '#ff9475', '#a8b294', '#d99368', '#c0b9df'],
    entityArtworkColors: ['#6969b3', '#817bd1', '#9d78c7', '#c8799e', '#e98272', '#ff9b73'],
    darkEntityArtworkColors: ['#7772c9', '#908be5', '#aa82d1', '#d17ea5', '#ea826f', '#ff9475'],
    entityArtworkImage: '/theme-entity-artwork/vuey.png',
};

export const DEFAULT_WORKSPACE_THEME_ID: WorkspaceThemeId = 'studio';

export const WORKSPACE_APP_THEMES: Record<WorkspaceAppThemeDefinition['id'], WorkspaceAppThemeDefinition> = {
    studio: {
        id: 'studio',
        label: 'Vuey',
        description: 'Playful, creative and polished without becoming visually noisy.',
        personality: 'Creative and optimistic',
        theme: STUDIO_APP_THEME,
    },
    'coffee-house': {
        id: 'coffee-house',
        label: 'Coffee House',
        description: 'A warm, tactile workspace with oat, clay, caramel and sage.',
        personality: 'Cosy and handcrafted',
        theme: {
            primary: '#845b46',
            primaryForeground: '#fffaf5',
            accent: '#c98059',
            accentForeground: '#321d13',
            surface: '#fcf9f5',
            surfaceMuted: '#ece5dc',
            background: '#f5f1eb',
            foreground: '#302721',
            ring: '#a57358',
            sidebar: '#fcf9f5',
            sidebarAccent: '#eee8e1',
            sidebarForeground: '#302721',
            portalGlow: '#eee1d8',
            darkBackground: '#17120f',
            darkForeground: '#f3ece5',
            darkSurface: '#211a16',
            darkSurfaceMuted: '#2a211c',
            darkRing: '#d0a088',
            darkSidebar: '#211a16',
            darkSidebarAccent: '#2a211c',
            darkSidebarForeground: '#f3ece5',
            darkPrimary: '#c18d70',
            darkPrimaryForeground: '#1c110d',
            darkAccent: '#d99368',
            darkAccentForeground: '#21120c',
            border: '#ded4c8',
            borderStrong: '#cdbfb0',
            mutedForeground: '#796c62',
            primarySoft: '#eee1d8',
            accentSoft: '#f8e4d8',
            darkBorder: '#3b3029',
            darkBorderStrong: '#524137',
            darkMutedForeground: '#b3a69b',
            darkPrimarySoft: '#453027',
            darkAccentSoft: '#503126',
            displayFontFamily: 'Fraunces',
            bodyFontFamily: 'Manrope',
            atmosphereImage: '/theme-atmospheres/coffee-house.svg',
            atmosphereColor: '#845b46',
            darkAtmosphereColor: '#c18d70',
            atmosphereOpacity: 0.08,
            darkAtmosphereOpacity: 0.027,
            radius: '1.125rem',
            controlRadius: '0.75rem',
            surfaceShadow: '0 18px 45px rgb(65 44 29 / 0.07)',
            darkSurfaceShadow: '0 18px 50px rgb(0 0 0 / 0.16)',
            chartColors: ['#845b46', '#c98059', '#74806b', '#a57358', '#6e5d8f'],
            darkChartColors: ['#c18d70', '#d99368', '#8fa084', '#d0a088', '#a59ff0'],
            entityArtworkColors: ['#684737', '#845b46', '#a57358', '#b87955', '#74806b', '#8e9a7d'],
            darkEntityArtworkColors: ['#8f604b', '#aa765d', '#c18d70', '#d09a73', '#7f8d73', '#99a889'],
            entityArtworkImage: '/theme-entity-artwork/coffee-house.png',
        },
    },
    editorial: {
        id: 'editorial',
        label: 'Editorial',
        description: 'A precise, design-led workspace with crisp hierarchy and fine rules.',
        personality: 'Sophisticated and structured',
        theme: {
            primary: '#29263f',
            primaryForeground: '#ffffff',
            accent: '#a34650',
            accentForeground: '#ffffff',
            surface: '#ffffff',
            surfaceMuted: '#efefeb',
            background: '#f7f7f5',
            foreground: '#171719',
            ring: '#55506f',
            sidebar: '#ffffff',
            sidebarAccent: '#efefeb',
            sidebarForeground: '#171719',
            portalGlow: '#eae8f0',
            darkBackground: '#111112',
            darkForeground: '#f2f1ed',
            darkSurface: '#19191b',
            darkSurfaceMuted: '#222225',
            darkRing: '#c0b9df',
            darkSidebar: '#19191b',
            darkSidebarAccent: '#222225',
            darkSidebarForeground: '#f2f1ed',
            darkPrimary: '#aaa3d0',
            darkPrimaryForeground: '#17151f',
            darkAccent: '#d0717b',
            darkAccentForeground: '#241214',
            border: '#dadad4',
            borderStrong: '#c7c7c0',
            mutedForeground: '#68686d',
            primarySoft: '#eae8f0',
            accentSoft: '#f4e4e6',
            darkBorder: '#343438',
            darkBorderStrong: '#48484d',
            darkMutedForeground: '#a5a4a0',
            darkPrimarySoft: '#343044',
            darkAccentSoft: '#46282c',
            displayFontFamily: 'Newsreader',
            bodyFontFamily: 'Schibsted Grotesk',
            metadataFontFamily: 'Space Mono',
            atmosphereImage: '/theme-atmospheres/editorial.svg',
            atmosphereColor: '#29263f',
            darkAtmosphereColor: '#aaa3d0',
            atmosphereOpacity: 0.07,
            darkAtmosphereOpacity: 0.02,
            radius: '0.625rem',
            controlRadius: '0.5rem',
            surfaceShadow: 'none',
            darkSurfaceShadow: 'none',
            chartColors: ['#29263f', '#a34650', '#55506f', '#8a9577', '#b68f6a'],
            darkChartColors: ['#aaa3d0', '#d0717b', '#c0b9df', '#a8b294', '#d0a77f'],
            entityArtworkColors: ['#172554', '#1e3557', '#294a6b', '#35627f', '#557b96', '#7898ae'],
            darkEntityArtworkColors: ['#263b68', '#31547a', '#477195', '#628ba8', '#7da1ba', '#9ab7ca'],
            entityArtworkImage: '/theme-entity-artwork/editorial.png',
        },
    },
    stone: {
        id: 'stone',
        label: 'Stone',
        description: 'A quiet, balanced workspace with natural low-saturation accents.',
        personality: 'Understated and calm',
        theme: {
            primary: '#706981',
            primaryForeground: '#ffffff',
            accent: '#8a957d',
            accentForeground: '#1c211a',
            surface: '#fcfbf9',
            surfaceMuted: '#eae8e3',
            background: '#f5f4f1',
            foreground: '#292a28',
            ring: '#81788f',
            sidebar: '#fcfbf9',
            sidebarAccent: '#eae8e3',
            sidebarForeground: '#292a28',
            portalGlow: '#e8e5ec',
            darkBackground: '#191816',
            darkForeground: '#efece6',
            darkSurface: '#22211e',
            darkSurfaceMuted: '#2b2925',
            darkRing: '#b4abbc',
            darkSidebar: '#22211e',
            darkSidebarAccent: '#2b2925',
            darkSidebarForeground: '#efece6',
            darkPrimary: '#a39aad',
            darkPrimaryForeground: '#18151b',
            darkAccent: '#99a48d',
            darkAccentForeground: '#171b15',
            border: '#dad6cf',
            borderStrong: '#c7c2b9',
            mutedForeground: '#72736e',
            primarySoft: '#e8e5ec',
            accentSoft: '#e7ebe2',
            darkBorder: '#3a3832',
            darkBorderStrong: '#4d4941',
            darkMutedForeground: '#aaa69e',
            darkPrimarySoft: '#3b3640',
            darkAccentSoft: '#343c31',
            displayFontFamily: 'Hanken Grotesk',
            bodyFontFamily: 'Work Sans',
            atmosphereImage: '/theme-atmospheres/stone.svg',
            atmosphereColor: '#706981',
            darkAtmosphereColor: '#a39aad',
            atmosphereOpacity: 0.08,
            darkAtmosphereOpacity: 0.021,
            radius: '0.875rem',
            controlRadius: '0.625rem',
            surfaceShadow: '0 14px 40px rgb(42 39 34 / 0.04)',
            darkSurfaceShadow: '0 16px 44px rgb(0 0 0 / 0.12)',
            chartColors: ['#706981', '#8a957d', '#b79b82', '#a39aad', '#81788f'],
            darkChartColors: ['#a39aad', '#99a48d', '#c0a186', '#b4abbc', '#8fa084'],
            entityArtworkColors: ['#535964', '#656c77', '#747c87', '#858b94', '#6b727a', '#96999d'],
            darkEntityArtworkColors: ['#676e79', '#78818d', '#89929d', '#9ca3ab', '#757e86', '#abb0b5'],
            entityArtworkImage: '/theme-entity-artwork/stone.png',
        },
    },
};

function clampChannel(value: number): number {
    return Math.max(0, Math.min(255, Math.round(value)));
}

function normalizeHexColor(value?: string | null): string | null {
    const raw = String(value || '').trim();
    if (!raw) return null;

    const normalized = raw.startsWith('#') ? raw : `#${raw}`;
    if (/^#[0-9a-f]{3}$/i.test(normalized)) {
        const [, chars] = normalized.match(/^#([0-9a-f]{3})$/i) || [];
        if (!chars) return null;
        return `#${chars.split('').map(char => `${char}${char}`).join('').toLowerCase()}`;
    }

    if (/^#[0-9a-f]{6}$/i.test(normalized)) {
        return normalized.toLowerCase();
    }

    return null;
}

function hexToRgb(value: string): Rgb {
    const normalized = normalizeHexColor(value) || '#000000';
    return {
        r: Number.parseInt(normalized.slice(1, 3), 16),
        g: Number.parseInt(normalized.slice(3, 5), 16),
        b: Number.parseInt(normalized.slice(5, 7), 16),
    };
}

function rgbToHex(rgb: Rgb): string {
    return `#${[rgb.r, rgb.g, rgb.b].map(channel => clampChannel(channel).toString(16).padStart(2, '0')).join('')}`;
}

function mixHex(colorA: string, colorB: string, ratioOfColorB: number): string {
    const a = hexToRgb(colorA);
    const b = hexToRgb(colorB);
    const ratio = Math.max(0, Math.min(1, ratioOfColorB));
    const ratioOfColorA = 1 - ratio;

    return rgbToHex({
        r: a.r * ratioOfColorA + b.r * ratio,
        g: a.g * ratioOfColorA + b.g * ratio,
        b: a.b * ratioOfColorA + b.b * ratio,
    });
}

export function resolveBrandSurfacePalette(surfaceColor?: string | null): BrandSurfacePalette {
    const seed = normalizeHexColor(surfaceColor) || DEFAULT_PORTAL_SURFACE_SEED;
    const darkBackground = mixHex(seed, '#080a0d', 0.86);

    return {
        seed,
        // The canvas is the quietest tint; cards retain slightly more of the tenant colour.
        background: mixHex(seed, '#ffffff', 0.82),
        surface: mixHex(seed, '#ffffff', 0.68),
        surfaceMuted: mixHex(seed, '#ffffff', 0.56),
        // Derive dark surfaces from the dark canvas so pale tenant seeds cannot create grey panels.
        darkBackground,
        darkSurface: mixHex(darkBackground, '#ffffff', 0.04),
        darkSurfaceMuted: mixHex(darkBackground, '#ffffff', 0.07),
    };
}

function relativeLuminance(color: string): number {
    const { r, g, b } = hexToRgb(color);
    const channels = [r, g, b].map((channel) => {
        const normalized = channel / 255;
        return normalized <= 0.03928
            ? normalized / 12.92
            : ((normalized + 0.055) / 1.055) ** 2.4;
    });

    return 0.2126 * channels[0]! + 0.7152 * channels[1]! + 0.0722 * channels[2]!;
}

function getReadableForeground(background: string): string {
    return relativeLuminance(background) > 0.45 ? '#162033' : '#ffffff';
}

export function resolveWorkspaceBrandTheme(tenant?: Tenant | null): WorkspaceBrandTheme {
    const configuredPrimary = normalizeHexColor(tenant?.paymentSettings?.brandPrimaryColor);
    const configuredAccent = normalizeHexColor(tenant?.paymentSettings?.brandAccentColor);
    const primary = configuredPrimary || DEFAULT_PORTAL_THEME.primary;
    const accent = configuredAccent || mixHex(primary, '#ffffff', 0.9);
    const surfaceSeed = normalizeHexColor(tenant?.paymentSettings?.brandSurfaceColor)
        || configuredAccent
        || (configuredPrimary ? accent : DEFAULT_PORTAL_SURFACE_SEED);
    const surfaces = resolveBrandSurfacePalette(surfaceSeed);
    const lightInteractionSurface = mixHex(surfaces.surface, accent, 0.12);
    const darkInteractionSurface = mixHex(surfaces.darkSurface, accent, 0.08);

    return {
        primary,
        primaryForeground: getReadableForeground(primary),
        accent,
        accentForeground: getReadableForeground(accent),
        surface: surfaces.surface,
        surfaceMuted: surfaces.surfaceMuted,
        background: surfaces.background,
        foreground: getReadableForeground(surfaces.background),
        ring: mixHex(primary, '#ffffff', 0.72),
        sidebar: surfaces.surface,
        sidebarAccent: lightInteractionSurface,
        sidebarForeground: getReadableForeground(surfaces.surface),
        portalGlow: mixHex(surfaces.surfaceMuted, accent, 0.12),
        darkBackground: surfaces.darkBackground,
        darkForeground: getReadableForeground(surfaces.darkBackground),
        darkSurface: surfaces.darkSurface,
        darkSurfaceMuted: surfaces.darkSurfaceMuted,
        darkRing: mixHex(primary, '#dbeafe', 0.4),
        darkSidebar: surfaces.darkSurface,
        darkSidebarAccent: darkInteractionSurface,
        darkSidebarForeground: getReadableForeground(surfaces.darkSurface),
    };
}

export function normalizePortalFontFamily(value: unknown, fallback: string): string {
    const candidate = String(value || '').trim();
    return /^[A-Za-z0-9 .&'()+-]{1,80}$/.test(candidate) ? candidate : fallback;
}

export function resolveClientFacingLogoUrl(tenant?: {
    tenantImageURL?: string;
    paymentSettings?: {
        invoiceLogoURL?: string;
    };
} | null): string {
    return String(
        tenant?.paymentSettings?.invoiceLogoURL
        || tenant?.tenantImageURL
        || '',
    ).trim();
}

export function portalFontFamilyStack(family: string): string {
    if (family === 'KoHo') return KOHO_FONT_FAMILY;
    if (family === 'Inter') return INTER_FONT_FAMILY;
    if (family === 'System UI') return SYSTEM_FONT_FAMILY;

    const normalized = normalizePortalFontFamily(family, DEFAULT_PORTAL_BODY_FONT_FAMILY);
    return `"${normalized}", "Inter Variable", "Inter", system-ui, sans-serif`;
}

export function resolvePortalTypography(tenant?: Tenant | null) {
    const displayFontFamily = normalizePortalFontFamily(
        tenant?.portalBranding?.displayFontFamily
            || LEGACY_PORTAL_FONT_FAMILIES[String(tenant?.portalBranding?.displayFontId || '')],
        DEFAULT_PORTAL_DISPLAY_FONT_FAMILY,
    );
    const bodyFontFamily = normalizePortalFontFamily(
        tenant?.portalBranding?.bodyFontFamily
            || LEGACY_PORTAL_FONT_FAMILIES[String(tenant?.portalBranding?.bodyFontId || '')],
        DEFAULT_PORTAL_BODY_FONT_FAMILY,
    );

    return {
        displayFontFamily,
        bodyFontFamily,
        displayFamily: portalFontFamilyStack(displayFontFamily),
        bodyFamily: portalFontFamilyStack(bodyFontFamily),
    };
}

export function resolvePortalBrandingContext(
    tenantId: string,
    publicBranding?: Partial<Tenant> | null,
    tenant?: Tenant | null,
): Partial<Tenant> | Tenant | null {
    if (publicBranding?.id === tenantId) return publicBranding;
    return tenant || null;
}

export function portalTypographyCssVars(tenant?: Tenant | null): Record<string, string> {
    const typography = resolvePortalTypography(tenant);
    return {
        '--font-display': typography.displayFamily,
        '--font-sans': typography.bodyFamily,
    };
}

function ensureGoogleFontFamiliesLoaded(fontFamilies: ReadonlyArray<string>, linkId: string): void {
    if (typeof document === 'undefined') return;

    const familyQueries = [...new Set(
        fontFamilies
            .map(family => normalizePortalFontFamily(family, ''))
            .filter(family => family && !['Inter', 'KoHo', 'System UI'].includes(family))
            .map(family => encodeURIComponent(family).replace(/%20/g, '+')),
    )];
    const existingLink = document.getElementById(linkId) as HTMLLinkElement | null;

    if (familyQueries.length === 0) {
        existingLink?.remove();
        return;
    }

    const href = `https://fonts.googleapis.com/css2?${familyQueries.map(query => `family=${query}:wght@400;500;600;700`).join('&')}&display=swap`;
    const link = existingLink || document.createElement('link');
    link.id = linkId;
    link.rel = 'stylesheet';
    link.href = href;

    if (!existingLink) {
        document.head.appendChild(link);
    }
}

export function ensurePortalFontFamiliesLoaded(fontFamilies: ReadonlyArray<string>): void {
    ensureGoogleFontFamiliesLoaded(fontFamilies, 'vuey-portal-google-fonts');
}

export function ensurePortalFontsLoaded(tenant?: Tenant | null): void {
    const typography = resolvePortalTypography(tenant);
    ensurePortalFontFamiliesLoaded([typography.displayFontFamily, typography.bodyFontFamily]);
}

export function resolveUserAppThemeDefinition(themeId?: WorkspaceThemeId | null): WorkspaceAppThemeDefinition {
    return WORKSPACE_APP_THEMES[themeId || DEFAULT_WORKSPACE_THEME_ID]
        || WORKSPACE_APP_THEMES[DEFAULT_WORKSPACE_THEME_ID];
}

export function resolveUserAppTheme(themeId?: WorkspaceThemeId | null): WorkspaceBrandTheme {
    return resolveUserAppThemeDefinition(themeId).theme;
}

export function ensureWorkspaceAppThemeFontsLoaded(themeId?: WorkspaceThemeId | null): void {
    const theme = resolveUserAppTheme(themeId);
    ensureGoogleFontFamiliesLoaded(
        [theme.displayFontFamily || '', theme.bodyFontFamily || '', theme.metadataFontFamily || ''],
        'vuey-app-theme-google-fonts',
    );
}

export function workspaceBrandThemeCssVars(theme: WorkspaceBrandTheme): Record<string, string> {
    const chartColors = theme.chartColors || [theme.primary, theme.accent, '#8a9577', '#b68f6a', '#dcc7ae'];
    const darkChartColors = theme.darkChartColors || [theme.darkPrimary || theme.primary, theme.darkAccent || theme.accent, '#a8b294', '#d0a77f', '#dcc7ae'];
    const entityArtworkColors = theme.entityArtworkColors || [theme.primary, theme.accent, theme.primarySoft || theme.surfaceMuted, theme.ring, theme.portalGlow, theme.surfaceMuted];
    const darkEntityArtworkColors = theme.darkEntityArtworkColors || [theme.darkPrimary || theme.primary, theme.darkAccent || theme.accent, theme.darkPrimarySoft || theme.darkSurfaceMuted, theme.darkRing, theme.darkAccentSoft || theme.darkSurfaceMuted, theme.darkSurfaceMuted];
    const entityArtworkImage = theme.entityArtworkImage || STUDIO_APP_THEME.entityArtworkImage!;
    const variables: Record<string, string> = {
        '--brand-primary': theme.primary,
        '--brand-primary-foreground': theme.primaryForeground,
        '--brand-accent': theme.accent,
        '--brand-accent-foreground': theme.accentForeground,
        '--brand-surface': theme.surface,
        '--brand-surface-muted': theme.surfaceMuted,
        '--brand-background': theme.background,
        '--brand-foreground': theme.foreground,
        '--brand-ring': theme.ring,
        '--brand-interactive-text': mixHex(theme.accent, theme.foreground, 0.48),
        '--brand-border': theme.border || mixHex(theme.background, theme.foreground, 0.1),
        '--brand-border-strong': theme.borderStrong || mixHex(theme.background, theme.foreground, 0.18),
        '--brand-muted-foreground': theme.mutedForeground || mixHex(theme.background, theme.foreground, 0.62),
        '--brand-primary-soft': theme.primarySoft || mixHex(theme.background, theme.primary, 0.12),
        '--brand-accent-soft': theme.accentSoft || mixHex(theme.background, theme.accent, 0.12),
        '--brand-sidebar': theme.sidebar,
        '--brand-sidebar-accent': theme.sidebarAccent,
        '--brand-sidebar-foreground': theme.sidebarForeground,
        '--brand-portal-glow': theme.portalGlow,
        '--brand-dark-background': theme.darkBackground,
        '--brand-dark-foreground': theme.darkForeground,
        '--brand-dark-surface': theme.darkSurface,
        '--brand-dark-surface-muted': theme.darkSurfaceMuted,
        '--brand-dark-primary': theme.darkPrimary || theme.primary,
        '--brand-dark-primary-foreground': theme.darkPrimaryForeground || theme.primaryForeground,
        '--brand-dark-accent': theme.darkAccent || theme.accent,
        '--brand-dark-accent-foreground': theme.darkAccentForeground || theme.accentForeground,
        '--brand-dark-ring': theme.darkRing,
        '--brand-dark-interactive-text': mixHex(theme.darkAccent || theme.accent, theme.darkForeground, 0.48),
        '--brand-dark-border': theme.darkBorder || mixHex(theme.darkBackground, theme.darkForeground, 0.11),
        '--brand-dark-border-strong': theme.darkBorderStrong || mixHex(theme.darkBackground, theme.darkForeground, 0.2),
        '--brand-dark-muted-foreground': theme.darkMutedForeground || mixHex(theme.darkBackground, theme.darkForeground, 0.66),
        '--brand-dark-primary-soft': theme.darkPrimarySoft || mixHex(theme.darkBackground, theme.darkPrimary || theme.primary, 0.22),
        '--brand-dark-accent-soft': theme.darkAccentSoft || mixHex(theme.darkBackground, theme.darkAccent || theme.accent, 0.22),
        '--brand-dark-sidebar': theme.darkSidebar,
        '--brand-dark-sidebar-accent': theme.darkSidebarAccent,
        '--brand-dark-sidebar-foreground': theme.darkSidebarForeground,
        '--brand-chart-1': chartColors[0],
        '--brand-chart-2': chartColors[1],
        '--brand-chart-3': chartColors[2],
        '--brand-chart-4': chartColors[3],
        '--brand-chart-5': chartColors[4],
        '--brand-dark-chart-1': darkChartColors[0],
        '--brand-dark-chart-2': darkChartColors[1],
        '--brand-dark-chart-3': darkChartColors[2],
        '--brand-dark-chart-4': darkChartColors[3],
        '--brand-dark-chart-5': darkChartColors[4],
        '--brand-entity-artwork-1': entityArtworkColors[0],
        '--brand-entity-artwork-2': entityArtworkColors[1],
        '--brand-entity-artwork-3': entityArtworkColors[2],
        '--brand-entity-artwork-4': entityArtworkColors[3],
        '--brand-entity-artwork-5': entityArtworkColors[4],
        '--brand-entity-artwork-6': entityArtworkColors[5],
        '--brand-dark-entity-artwork-1': darkEntityArtworkColors[0],
        '--brand-dark-entity-artwork-2': darkEntityArtworkColors[1],
        '--brand-dark-entity-artwork-3': darkEntityArtworkColors[2],
        '--brand-dark-entity-artwork-4': darkEntityArtworkColors[3],
        '--brand-dark-entity-artwork-5': darkEntityArtworkColors[4],
        '--brand-dark-entity-artwork-6': darkEntityArtworkColors[5],
        '--brand-entity-artwork-image': `url("${entityArtworkImage}")`,
    };

    if (theme.displayFontFamily) variables['--font-display'] = portalFontFamilyStack(theme.displayFontFamily);
    if (theme.bodyFontFamily) variables['--font-sans'] = portalFontFamilyStack(theme.bodyFontFamily);
    if (theme.metadataFontFamily) variables['--font-metadata'] = portalFontFamilyStack(theme.metadataFontFamily);
    if (theme.atmosphereImage) variables['--theme-atmosphere-image'] = `url("${theme.atmosphereImage}")`;
    if (theme.atmosphereColor) variables['--theme-atmosphere-color'] = theme.atmosphereColor;
    if (theme.darkAtmosphereColor) variables['--theme-dark-atmosphere-color'] = theme.darkAtmosphereColor;
    if (typeof theme.atmosphereOpacity === 'number') variables['--theme-atmosphere-opacity'] = String(theme.atmosphereOpacity);
    if (typeof theme.darkAtmosphereOpacity === 'number') variables['--theme-dark-atmosphere-opacity'] = String(theme.darkAtmosphereOpacity);
    if (theme.radius) variables['--radius'] = theme.radius;
    if (theme.controlRadius) variables['--control-radius'] = theme.controlRadius;
    if (theme.surfaceShadow) variables['--app-surface-shadow'] = theme.surfaceShadow;
    if (theme.darkSurfaceShadow) variables['--app-dark-surface-shadow'] = theme.darkSurfaceShadow;
    return variables;
}

export function applyWorkspaceBrandTheme(tenant?: Tenant | null): WorkspaceBrandTheme {
    const theme = resolveWorkspaceBrandTheme(tenant);

    if (typeof document === 'undefined') {
        return theme;
    }

    const root = document.documentElement;
    Object.entries(workspaceBrandThemeCssVars(theme)).forEach(([key, value]) => {
        root.style.setProperty(key, value);
    });
    Object.entries(portalTypographyCssVars(tenant)).forEach(([key, value]) => {
        root.style.setProperty(key, value);
    });
    ensurePortalFontsLoaded(tenant);

    return theme;
}

export { DEFAULT_PORTAL_THEME as defaultWorkspaceBrandTheme, normalizeHexColor };
